import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../../components/UI/Button';
import { useAuth } from '../../contexts/AuthContext';
import { formService } from '../../services/formService';
import type {
  Form,
  FormField,
  FormResponse,
  FormResponseAnswer,
  FormTheme,
} from '../../types/form';
import { DEFAULT_CONFIRMATION_MESSAGE, isDisplayOnlyFieldType, resolveFormTheme } from '../../types/form';
import {
  canonicalizeMaskedValue,
  resolveMaskSettings,
  validateMaskedValue,
} from '../../types/formFieldSettings';
import { FormFieldRenderer } from './FormFieldRenderer';
import {
  buildFormThemeCssVars,
  ensureFormThemeFontsLoaded,
} from './FormThemePreview';
import { getSectionFields, resolveNextSection, sortSections } from './formNavigation';

type PageState =
  | 'loading'
  | 'ready'
  | 'notFound'
  | 'loginRequired'
  | 'closed'
  | 'submitted'
  | 'alreadyResponded';

const FORM_REQUIRES_AUTH_CODE = 'FORM_REQUIRES_AUTH';

function responseIdStorageKey(publicId: string): string {
  return `know.form.responseId.${publicId}`;
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  const response = (
    error as {
      response?: { data?: { message?: string; error?: string; title?: string; detail?: string } };
    }
  )?.response?.data;

  if (response?.error) {
    return response.error;
  }
  if (response?.message) {
    return response.message;
  }
  if (response?.detail) {
    return response.detail;
  }
  if (response?.title) {
    return response.title;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

function isFormRequiresAuthError(error: unknown): boolean {
  const axiosError = error as {
    response?: { status?: number; data?: { code?: string } };
  };
  if (axiosError.response?.status !== 401) {
    return false;
  }

  const code = axiosError.response?.data?.code;
  if (!code) {
    return true;
  }

  return code === FORM_REQUIRES_AUTH_CODE;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Não foi possível ler o arquivo.'));
        return;
      }

      const commaIndex = result.indexOf(',');
      if (commaIndex >= 0) {
        resolve(result.slice(commaIndex + 1));
        return;
      }

      resolve(result);
    };
    reader.onerror = () => {
      reject(new Error('Não foi possível ler o arquivo.'));
    };
    reader.readAsDataURL(file);
  });
}

const FORM_SESSION_STORAGE_KEY = 'know.form.respondentSessionId';

function getOrCreateRespondentSessionId(): string {
  try {
    const existing = window.localStorage.getItem(FORM_SESSION_STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const created =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `sess-${Date.now()}`;
    window.localStorage.setItem(FORM_SESSION_STORAGE_KEY, created);
    return created;
  } catch {
    return `sess-${Date.now()}`;
  }
}

function PublicFormTopBar({ primaryColor }: { primaryColor: string }) {
  return (
    <header
      className="border-b text-white shadow-sm"
      style={{
        backgroundColor: primaryColor,
        borderColor: primaryColor,
      }}
    >
      <div className="mx-auto flex h-14 max-w-3xl items-center gap-2 px-4">
        <img className="h-7 w-auto" src="/know-logo.png" alt="Baruk Know" />
        <span className="font-bold text-lg">Baruk Know</span>
        <span className="ml-2 hidden text-sm text-white/80 sm:inline">
          Formulários
        </span>
      </div>
    </header>
  );
}

function PublicFormShell({
  theme,
  children,
}: {
  theme: FormTheme;
  children: React.ReactNode;
}) {
  const resolved = resolveFormTheme(theme);

  useEffect(() => {
    ensureFormThemeFontsLoaded();
  }, []);

  return (
    <div className="min-h-screen" style={buildFormThemeCssVars(resolved)}>
      <PublicFormTopBar primaryColor={resolved.primaryColor} />
      <div className="px-4 py-8">{children}</div>
    </div>
  );
}

export const PublicFormPage: React.FC = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const { user } = useAuth();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [form, setForm] = useState<Form | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [existingFileNames, setExistingFileNames] = useState<
    Record<string, string>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentSectionId, setCurrentSectionId] = useState<string | null>(null);
  const [sectionHistory, setSectionHistory] = useState<string[]>([]);
  const [confirmationMessage, setConfirmationMessage] = useState(
    DEFAULT_CONFIRMATION_MESSAGE
  );
  const [showSubmitAnother, setShowSubmitAnother] = useState(false);
  const [sessionId] = useState(() => getOrCreateRespondentSessionId());
  const [draftSaveLabel, setDraftSaveLabel] = useState<string | null>(null);
  const [editingResponseId, setEditingResponseId] = useState<string | null>(
    null
  );
  const [canEditSubmitted, setCanEditSubmitted] = useState(false);
  const [submittedResponse, setSubmittedResponse] =
    useState<FormResponse | null>(null);

  const theme = useMemo(
    () => resolveFormTheme(form?.theme),
    [form?.theme]
  );

  const isLoggedIn = Boolean(user?.id);
  const showEmailField =
    Boolean(form)
    && form!.collectEmail !== 'NotCollect'
    && !isLoggedIn;

  const loginReturnPath = publicId
    ? `/forms/public/${publicId}`
    : '/forms/public';
  const loginHref = `/login?returnUrl=${encodeURIComponent(loginReturnPath)}`;

  const applyResponseToForm = useCallback(
    (data: Form, response: FormResponse) => {
      const nextAnswers: Record<string, string> = {};
      const nextFiles: Record<string, File | null> = {};
      const nextExistingFiles: Record<string, string> = {};

      data.fields.forEach((field) => {
        nextAnswers[field.id] = '';
        if (field.type === 'FileUpload') {
          nextFiles[field.id] = null;
        }
      });

      response.answers.forEach((answer) => {
        if (answer.valueText) {
          nextAnswers[answer.fieldId] = answer.valueText;
        }
        if (answer.documentContentId || answer.fileName) {
          nextExistingFiles[answer.fieldId] =
            answer.fileName || answer.valueText || 'Arquivo enviado';
        }
      });

      setAnswers(nextAnswers);
      setFiles(nextFiles);
      setExistingFileNames(nextExistingFiles);
      setEmail(response.respondentEmail ?? user?.email ?? '');
      setEditingResponseId(response.id);
      setCurrentSectionId(sortSections(data.sections)[0]?.id ?? null);
      setSectionHistory([]);
      setErrors({});
    },
    [user?.email]
  );

  const loadForm = useCallback(async () => {
    if (!publicId) {
      setPageState('notFound');
      return;
    }

    try {
      setPageState('loading');
      const data = await formService.getPublicByPublicId(publicId);
      setForm(data);
      const initialAnswers: Record<string, string> = {};
      const initialFiles: Record<string, File | null> = {};
      data.fields.forEach((field) => {
        initialAnswers[field.id] = '';
        if (field.type === 'FileUpload') {
          initialFiles[field.id] = null;
        }
      });
      setAnswers(initialAnswers);
      setFiles(initialFiles);
      setExistingFileNames({});
      setEditingResponseId(null);
      setSubmittedResponse(null);
      setCurrentSectionId(sortSections(data.sections)[0]?.id ?? null);
      setSectionHistory([]);
      setShowSubmitAnother(
        Boolean(data.showSubmitAnotherResponseLink) && !data.limitOneResponsePerUser
      );
      setConfirmationMessage(
        data.confirmationMessage?.trim() || DEFAULT_CONFIRMATION_MESSAGE
      );
      setCanEditSubmitted(Boolean(data.allowResponseEditing));

      if (user?.email && data.collectEmail !== 'NotCollect') {
        setEmail(user.email);
      } else {
        setEmail('');
      }

      if (user?.id) {
        try {
          const myResponse = await formService.getMyResponse(publicId);
          if (myResponse) {
            setSubmittedResponse(myResponse);
            setEditingResponseId(myResponse.id);
            setCanEditSubmitted(Boolean(data.allowResponseEditing));
            setPageState('alreadyResponded');
            return;
          }
        } catch {
          // segue para preenchimento novo
        }
      }

      if (data.allowDraftResponses) {
        try {
          const draft = await formService.getDraft(publicId, sessionId);
          if (draft?.draftData) {
            const parsed = JSON.parse(draft.draftData) as {
              answers?: Record<string, string>;
              email?: string;
              currentSectionId?: string;
            };
            if (parsed.answers) {
              setAnswers((prev) => ({ ...prev, ...parsed.answers }));
            }
            if (parsed.email && !user?.email) {
              setEmail(parsed.email);
            }
            if (
              parsed.currentSectionId
              && data.sections.some((s) => s.id === parsed.currentSectionId)
            ) {
              setCurrentSectionId(parsed.currentSectionId);
            }
          }
        } catch {
          // rascunho é best-effort
        }
      }

      setPageState('ready');
    } catch (error: unknown) {
      if (isFormRequiresAuthError(error)) {
        setPageState('loginRequired');
        return;
      }

      setPageState('notFound');
    }
  }, [publicId, sessionId, user?.id, user?.email]);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  useEffect(() => {
    if (!form || !publicId || pageState !== 'ready' || !form.allowDraftResponses) {
      return;
    }

    if (editingResponseId) {
      return;
    }

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          await formService.saveDraft(publicId, {
            respondentSessionId: sessionId,
            respondentEmail: showEmailField
              ? email.trim() || undefined
              : undefined,
            draftData: JSON.stringify({
              answers,
              email: showEmailField ? email : undefined,
              currentSectionId,
            }),
          });
          setDraftSaveLabel('Rascunho salvo');
        } catch {
          // best-effort
        }
      })();
    }, 1500);

    return () => window.clearTimeout(timer);
  }, [
    answers,
    email,
    currentSectionId,
    form,
    publicId,
    pageState,
    sessionId,
    editingResponseId,
    showEmailField,
  ]);

  const validateFields = (fieldsToValidate: FormField[]): boolean => {
    if (!form) {
      return false;
    }

    const nextErrors: Record<string, string> = {};

    if (showEmailField) {
      if (form.collectEmail === 'Required' && !email.trim()) {
        nextErrors._email = 'Informe seu e-mail.';
      } else if (
        email.trim()
        && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
      ) {
        nextErrors._email = 'Informe um e-mail válido.';
      }
    }

    fieldsToValidate.forEach((field) => {
      if (isDisplayOnlyFieldType(field.type)) {
        return;
      }

      if (field.type === 'FileUpload') {
        const file = files[field.id];
        const hasExisting = Boolean(existingFileNames[field.id]);
        if (field.isRequired && !file && !hasExisting) {
          nextErrors[field.id] = 'Esta pergunta é obrigatória.';
        }
        return;
      }

      const value = (answers[field.id] ?? '').trim();

      if (field.isRequired && !value) {
        nextErrors[field.id] = 'Esta pergunta é obrigatória.';
        return;
      }

      if (value && field.type === 'ShortText') {
        const maskError = validateMaskedValue(
          value,
          resolveMaskSettings(field),
          field.title
        );
        if (maskError) {
          nextErrors[field.id] = maskError;
          return;
        }
      }

      if (value && field.validationRegex) {
        try {
          const regex = new RegExp(field.validationRegex);
          const canonical =
            field.type === 'ShortText'
              ? canonicalizeMaskedValue(value, resolveMaskSettings(field))
              : value;
          if (!regex.test(canonical)) {
            nextErrors[field.id] =
              field.validationMessage || 'Resposta inválida.';
          }
        } catch {
          // Regex inválida no builder — ignora na validação pública
        }
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const currentSectionFields = (): FormField[] => {
    if (!form || !currentSectionId) {
      return form?.fields ?? [];
    }
    return getSectionFields(form.fields, currentSectionId);
  };

  const handleGoNext = () => {
    if (!form || !currentSectionId) {
      return;
    }

    if (!validateFields(currentSectionFields())) {
      return;
    }

    const next = resolveNextSection(form, currentSectionId, answers);
    if (next.kind === 'submit') {
      void submitForm();
      return;
    }

    setSectionHistory((prev) => [...prev, currentSectionId]);
    setCurrentSectionId(next.sectionId);
    setErrors({});
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleGoBack = () => {
    setSectionHistory((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      const history = [...prev];
      const previousSectionId = history.pop() as string;
      setCurrentSectionId(previousSectionId);
      setErrors({});
      return history;
    });
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const buildAnswersPayload = async (
    includeEmptyText: boolean
  ): Promise<FormResponseAnswer[]> => {
    if (!form) {
      return [];
    }

    const payload: FormResponseAnswer[] = [];

    for (const field of form.fields) {
      if (isDisplayOnlyFieldType(field.type)) {
        continue;
      }

      if (field.type === 'FileUpload') {
        const file = files[field.id];
        if (!file) {
          continue;
        }

        const fileBase64 = await fileToBase64(file);
        payload.push({
          fieldId: field.id,
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          fileBase64,
        });
        continue;
      }

      const rawValue = answers[field.id] ?? '';
      if (!includeEmptyText && !rawValue.trim()) {
        continue;
      }

      const valueText =
        field.type === 'ShortText'
          ? canonicalizeMaskedValue(rawValue, resolveMaskSettings(field))
          : rawValue;

      payload.push({
        fieldId: field.id,
        valueText,
      });
    }

    return payload;
  };

  const resolveRespondentEmail = (): string | undefined => {
    if (!form || form.collectEmail === 'NotCollect') {
      return undefined;
    }

    if (showEmailField) {
      return email.trim() || undefined;
    }

    return user?.email?.trim() || email.trim() || undefined;
  };

  const resetAnswers = () => {
    if (!form) {
      return;
    }

    const resetAnswersMap: Record<string, string> = {};
    const resetFiles: Record<string, File | null> = {};
    form.fields.forEach((field) => {
      resetAnswersMap[field.id] = '';
      if (field.type === 'FileUpload') {
        resetFiles[field.id] = null;
      }
    });
    setAnswers(resetAnswersMap);
    setFiles(resetFiles);
    setExistingFileNames({});
    setErrors({});
    setEditingResponseId(null);
    setSubmittedResponse(null);
    if (user?.email && form.collectEmail !== 'NotCollect') {
      setEmail(user.email);
    } else {
      setEmail('');
    }
    setCurrentSectionId(sortSections(form.sections)[0]?.id ?? null);
    setSectionHistory([]);
  };

  const startEditingResponse = (response: FormResponse) => {
    if (!form) {
      return;
    }

    applyResponseToForm(form, response);
    setSubmittedResponse(response);
    setCanEditSubmitted(Boolean(form.allowResponseEditing));
    setPageState('ready');
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const submitForm = async () => {
    if (!form || !publicId) {
      return;
    }

    if (!validateFields(currentSectionFields())) {
      return;
    }

    if (showEmailField) {
      if (form.collectEmail === 'Required' && !email.trim()) {
        setErrors((prev) => ({ ...prev, _email: 'Informe seu e-mail.' }));
        setCurrentSectionId(
          sortSections(form.sections)[0]?.id ?? currentSectionId
        );
        return;
      }

      if (
        email.trim()
        && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
      ) {
        setErrors((prev) => ({
          ...prev,
          _email: 'Informe um e-mail válido.',
        }));
        setCurrentSectionId(
          sortSections(form.sections)[0]?.id ?? currentSectionId
        );
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const isEditing = Boolean(editingResponseId && submittedResponse);
      const answersPayload = await buildAnswersPayload(isEditing);
      const payload = {
        respondentEmail: resolveRespondentEmail(),
        respondentSessionId: sessionId,
        answers: answersPayload,
      };

      const result =
        isEditing && editingResponseId
          ? await formService.updateResponse(
              publicId,
              editingResponseId,
              payload
            )
          : await formService.submitResponse(publicId, payload);

      setSubmittedResponse(result);
      setEditingResponseId(result.id);
      setCanEditSubmitted(
        Boolean(result.allowResponseEditing ?? form.allowResponseEditing)
      );
      setConfirmationMessage(
        result.confirmationMessage?.trim()
        || form.confirmationMessage?.trim()
        || DEFAULT_CONFIRMATION_MESSAGE
      );
      setShowSubmitAnother(
        Boolean(result.showSubmitAnotherResponseLink)
        && !form.limitOneResponsePerUser
      );

      try {
        window.localStorage.setItem(
          responseIdStorageKey(publicId),
          result.id
        );
      } catch {
        // best-effort
      }

      setPageState('submitted');
    } catch (error: unknown) {
      if (isFormRequiresAuthError(error)) {
        setPageState('loginRequired');
        return;
      }

      setErrors({
        _form: getApiErrorMessage(
          error,
          editingResponseId
            ? 'Não foi possível atualizar a resposta.'
            : 'Não foi possível enviar a resposta.'
        ),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleGoNext();
  };

  if (pageState === 'loading') {
    return (
      <PublicFormShell theme={theme}>
        <div className="flex justify-center py-20 text-gray-500">
          Carregando formulário...
        </div>
      </PublicFormShell>
    );
  }

  if (pageState === 'loginRequired') {
    return (
      <PublicFormShell theme={theme}>
        <div className="mx-auto w-full max-w-lg rounded-lg border border-gray-200 bg-white p-8 text-center">
          <h1 className="text-xl font-semibold text-gray-900">
            Login necessário
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Este formulário é privado. Faça login para responder.
          </p>
          <div className="mt-6 flex justify-center">
            <Link to={loginHref}>
              <Button type="button" variant="primary">
                Entrar
              </Button>
            </Link>
          </div>
        </div>
      </PublicFormShell>
    );
  }

  if (pageState === 'notFound') {
    return (
      <PublicFormShell theme={theme}>
        <div className="mx-auto w-full max-w-lg rounded-lg border border-gray-200 bg-white p-8 text-center">
          <h1 className="text-xl font-semibold text-gray-900">
            Formulário não encontrado
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            O link pode estar incorreto, o formulário foi removido ou não está
            aceitando respostas.
          </p>
        </div>
      </PublicFormShell>
    );
  }

  if (pageState === 'closed') {
    return (
      <PublicFormShell theme={theme}>
        <div className="mx-auto w-full max-w-lg rounded-lg border border-gray-200 bg-white p-8 text-center">
          <h1 className="text-xl font-semibold text-gray-900">
            Formulário encerrado
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Este formulário não está aceitando respostas no momento.
          </p>
        </div>
      </PublicFormShell>
    );
  }

  if (pageState === 'submitted') {
    return (
      <PublicFormShell theme={theme}>
        <div className="mx-auto w-full max-w-lg rounded-lg border border-gray-200 bg-white p-8 text-center">
          <h1
            className="font-semibold text-gray-900"
            style={{ fontSize: theme.textSizes.title }}
          >
            Resposta enviada
          </h1>
          <p
            className="mt-3 text-gray-500"
            style={{ fontSize: theme.textSizes.body }}
          >
            {confirmationMessage}
          </p>
          <div className="mt-6 flex flex-col items-center gap-3">
            {canEditSubmitted && submittedResponse ? (
              <Button
                type="button"
                variant="primary"
                onClick={() => startEditingResponse(submittedResponse)}
              >
                Editar resposta
              </Button>
            ) : null}
            {showSubmitAnother ? (
              <button
                type="button"
                className="text-sm font-medium hover:opacity-80"
                style={{ color: theme.primaryColor }}
                onClick={() => {
                  resetAnswers();
                  setPageState('ready');
                }}
              >
                Enviar outra resposta
              </button>
            ) : null}
          </div>
        </div>
      </PublicFormShell>
    );
  }

  if (pageState === 'alreadyResponded') {
    return (
      <PublicFormShell theme={theme}>
        <div className="mx-auto w-full max-w-lg rounded-lg border border-gray-200 bg-white p-8 text-center">
          <h1
            className="font-semibold text-gray-900"
            style={{ fontSize: theme.textSizes.title }}
          >
            Você já respondeu
          </h1>
          <p
            className="mt-3 text-gray-500"
            style={{ fontSize: theme.textSizes.body }}
          >
            {canEditSubmitted
              ? 'Este formulário permite editar a resposta enviada.'
              : 'Este formulário não permite uma nova resposta deste usuário.'}
          </p>
          {canEditSubmitted && submittedResponse ? (
            <div className="mt-6 flex justify-center">
              <Button
                type="button"
                variant="primary"
                onClick={() => startEditingResponse(submittedResponse)}
              >
                Editar resposta
              </Button>
            </div>
          ) : null}
        </div>
      </PublicFormShell>
    );
  }

  if (!form) {
    return null;
  }

  const sortedSections = sortSections(form.sections);
  const hasMultipleSections = sortedSections.length > 1;
  const activeSectionId = currentSectionId ?? sortedSections[0]?.id ?? null;
  const activeSection = sortedSections.find((s) => s.id === activeSectionId);
  const activeSectionIndex = sortedSections.findIndex(
    (s) => s.id === activeSectionId
  );
  const visibleFields = activeSectionId
    ? getSectionFields(form.fields, activeSectionId)
    : form.fields.slice().sort((a, b) => a.order - b.order);

  const isEditingExisting = Boolean(editingResponseId && submittedResponse);
  const hasRequiredFields = visibleFields.some((field) => field.isRequired);
  const nextStep = activeSectionId
    ? resolveNextSection(form, activeSectionId, answers)
    : { kind: 'submit' as const };
  const isLastStep = nextStep.kind === 'submit';
  const canGoBack = sectionHistory.length > 0;
  const isFirstSectionCard = !hasMultipleSections || activeSectionIndex === 0;
  const progressPercent =
    hasMultipleSections && sortedSections.length > 0
      ? Math.round(
          ((Math.max(activeSectionIndex, 0) + 1) / sortedSections.length) * 100
        )
      : 100;

  return (
    <PublicFormShell theme={theme}>
      <form
        onSubmit={handleFormSubmit}
        className="mx-auto w-full max-w-3xl"
        noValidate
      >
        {isEditingExisting ? (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Você está editando uma resposta já enviada.
          </div>
        ) : null}

        {form.showProgressBar && hasMultipleSections ? (
          <div className="mb-4 overflow-hidden rounded-lg border border-gray-200 bg-white px-4 py-3">
            <div className="mb-2 flex items-center justify-between text-xs text-gray-600">
              <span>
                Progresso — seção {activeSectionIndex + 1} de{' '}
                {sortedSections.length}
              </span>
              <span>{progressPercent}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${progressPercent}%`,
                  backgroundColor: theme.primaryColor,
                }}
              />
            </div>
          </div>
        ) : null}

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          {theme.headerImageUrl && isFirstSectionCard ? (
            <img
              src={theme.headerImageUrl}
              alt=""
              className="h-44 w-full object-cover"
            />
          ) : null}

          {isFirstSectionCard && (
            <div className="space-y-2 border-b border-gray-100 px-6 py-5">
              <div
                className="mb-2 h-1.5 w-14 rounded-full"
                style={{ backgroundColor: theme.primaryColor }}
              />
              <h1
                className="font-semibold text-gray-900"
                style={{ fontSize: theme.textSizes.title }}
              >
                {form.title || 'Formulário sem título'}
              </h1>
              {form.description && (
                <p
                  className="text-gray-600"
                  style={{ fontSize: theme.textSizes.body }}
                >
                  {form.description}
                </p>
              )}
              {hasRequiredFields && (
                <p
                  className="text-gray-500"
                  style={{ fontSize: theme.textSizes.body }}
                >
                  Campos marcados com{' '}
                  <span className="font-medium text-gray-700">obrigatório</span>{' '}
                  precisam ser preenchidos.
                </p>
              )}
            </div>
          )}

          {hasMultipleSections && (
            <div className="border-b border-gray-100 px-6 py-4">
              <p
                className="text-xs font-medium uppercase tracking-wide"
                style={{ color: theme.primaryColor }}
              >
                Seção {activeSectionIndex + 1} de {sortedSections.length}
              </p>
              {activeSection?.title && (
                <h2
                  className="mt-1 font-semibold text-gray-900"
                  style={{ fontSize: theme.textSizes.section }}
                >
                  {activeSection.title}
                </h2>
              )}
              {activeSection?.description && (
                <p
                  className="mt-1 text-gray-600"
                  style={{ fontSize: theme.textSizes.body }}
                >
                  {activeSection.description}
                </p>
              )}
            </div>
          )}

          <div className="space-y-6 px-6 py-6">
            {isFirstSectionCard && showEmailField && (
              <div>
                <label
                  className="mb-1.5 block font-medium text-gray-800"
                  style={{ fontSize: theme.textSizes.question }}
                >
                  E-mail
                  {form.collectEmail === 'Required' ? (
                    <span className="ml-1 text-red-600">*</span>
                  ) : (
                    <span className="ml-1 text-xs font-normal text-gray-500">
                      (opcional)
                    </span>
                  )}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1"
                  style={{
                    fontSize: theme.textSizes.body,
                    ['--tw-ring-color' as string]: theme.primaryColor,
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = theme.primaryColor;
                    e.currentTarget.style.boxShadow = `0 0 0 1px ${theme.primaryColor}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '';
                    e.currentTarget.style.boxShadow = '';
                  }}
                  placeholder="seu@email.com"
                />
                {errors._email ? (
                  <p className="mt-1 text-sm text-red-600">{errors._email}</p>
                ) : null}
              </div>
            )}

            {isFirstSectionCard
              && isLoggedIn
              && form.collectEmail !== 'NotCollect' ? (
              <p
                className="text-sm text-gray-500"
                style={{ fontSize: theme.textSizes.body }}
              >
                Resposta vinculada ao e-mail da sua conta
                {user?.email ? ` (${user.email})` : ''}.
              </p>
            ) : null}

            {visibleFields.map((field) => (
              <div
                key={field.id}
                className="border-t border-gray-100 pt-6 first:border-t-0 first:pt-0"
                style={{ fontSize: theme.textSizes.question }}
              >
                <FormFieldRenderer
                  field={field}
                  value={
                    field.type === 'FileUpload'
                      ? files[field.id]?.name
                        || existingFileNames[field.id]
                        || answers[field.id]
                        || ''
                      : answers[field.id] ?? ''
                  }
                  error={errors[field.id]}
                  disabled={
                    field.type === 'FileUpload'
                    && Boolean(existingFileNames[field.id])
                  }
                  onChange={(value) => {
                    setAnswers((prev) => ({ ...prev, [field.id]: value }));
                    if (errors[field.id]) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next[field.id];
                        return next;
                      });
                    }
                  }}
                  onFileChange={
                    field.type === 'FileUpload'
                    && !existingFileNames[field.id]
                      ? (file) => {
                          setFiles((prev) => ({ ...prev, [field.id]: file }));
                          if (errors[field.id]) {
                            setErrors((prev) => {
                              const next = { ...prev };
                              delete next[field.id];
                              return next;
                            });
                          }
                        }
                      : undefined
                  }
                />
                {field.type === 'FileUpload' && existingFileNames[field.id] ? (
                  <p className="mt-2 text-xs text-gray-500">
                    Arquivo já enviado. A substituição não é permitida nesta
                    edição.
                  </p>
                ) : null}
              </div>
            ))}
          </div>

          {errors._form && (
            <div className="border-t border-gray-100 px-6 pb-2">
              <p className="text-sm text-red-600">{errors._form}</p>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 border-t border-gray-100 bg-gray-50 px-6 py-4">
            <div className="flex items-center gap-2">
              {canGoBack && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleGoBack}
                  disabled={isSubmitting}
                >
                  Voltar
                </Button>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
                style={{ backgroundColor: theme.primaryColor }}
              >
                {isSubmitting
                  ? isEditingExisting
                    ? 'Salvando...'
                    : 'Enviando...'
                  : isLastStep
                    ? isEditingExisting
                      ? 'Salvar alterações'
                      : 'Enviar'
                    : 'Próxima'}
              </button>
            </div>
            <div className="flex items-center gap-3">
              {draftSaveLabel && form.allowDraftResponses && !isEditingExisting ? (
                <span className="text-xs text-gray-500">{draftSaveLabel}</span>
              ) : null}
              {!isEditingExisting ? (
                <button
                  type="button"
                  className="text-sm text-gray-600 hover:text-gray-900"
                  onClick={resetAnswers}
                >
                  Limpar formulário
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </form>
    </PublicFormShell>
  );
};

export default PublicFormPage;
